import { IntentionItem } from "./types";

// =============================================================================
// LAYOUT CONFIGURATION - Modify these values to change layout dimensions
// =============================================================================

// Individual section widths (Personal & General Messages)
// Examples: "380px" (compact) | "420px" (current) | "480px" (spacious)
export const INTENTION_SECTION_WIDTH = ["100%", "100%", "520px"];

// Main container width on desktop
// Examples: "800px" (compact) | "900px" (current) | "1200px" (wide)
export const MAIN_CONTAINER_MAX_WIDTH = "960px";

// Message cards minimum width (usually same as INTENTION_SECTION_WIDTH)
export const MESSAGES_CARD_MIN_WIDTH = ["100%", "100%", "520px"];


// Responsive container width: [Mobile, Tablet, Desktop]
// Examples: ["100%", "100%", "800px"] | ["95%", "85%", "1200px"]
export const MAIN_CONTAINER_RESPONSIVE_WIDTH = ["100%", "100%", "900px"];

// Gap between sections: [Mobile, Tablet, Desktop] (Chakra UI spacing: 4=16px, 6=24px, 8=32px)
export const RESPONSIVE_SPACING = [4, 4, 6];

// Container padding: [Mobile, Tablet, Desktop] (spacing from screen edges)
export const RESPONSIVE_PADDING = [4, 4, 6];

// =============================================================================
// QUICK CHANGE EXAMPLES
// =============================================================================
// Wider sections: INTENTION_SECTION_WIDTH = "480px" + MAIN_CONTAINER_MAX_WIDTH = "1000px"
// Compact layout: INTENTION_SECTION_WIDTH = "350px" + MAIN_CONTAINER_MAX_WIDTH = "750px"
// More spacing: RESPONSIVE_SPACING = [6, 8, 10]
// =============================================================================

// =============================================================================
// LEGACY CONFIGURATION (KEEP FOR REFERENCE)
// =============================================================================

// Legacy breakpoints structure (keep for reference)
export const LAYOUT_BREAKPOINTS = {
  // Main container dimensions
  MAIN_CONTAINER: {
    MOBILE: "100%",
    TABLET: "100%", 
    DESKTOP: "900px",
  },
  // Individual intention section dimensions
  INTENTION_SECTION: {
    MOBILE: "100%",
    TABLET: "100%",
    DESKTOP: "420px",
  },
  // Spacing values
  SPACING: {
    MOBILE: 4,
    TABLET: 4,
    DESKTOP: 6,
  },
  // Padding values
  PADDING: {
    MOBILE: 4,
    TABLET: 4,
    DESKTOP: 6,
  }
} as const;

// Responsive arrays for Chakra UI components
export const RESPONSIVE_WIDTHS = {
  MAIN_CONTAINER: [
    LAYOUT_BREAKPOINTS.MAIN_CONTAINER.MOBILE,
    LAYOUT_BREAKPOINTS.MAIN_CONTAINER.TABLET,
    LAYOUT_BREAKPOINTS.MAIN_CONTAINER.DESKTOP
  ],
  INTENTION_SECTION: [
    LAYOUT_BREAKPOINTS.INTENTION_SECTION.MOBILE,
    LAYOUT_BREAKPOINTS.INTENTION_SECTION.TABLET,
    LAYOUT_BREAKPOINTS.INTENTION_SECTION.DESKTOP
  ],
  SPACING: [
    LAYOUT_BREAKPOINTS.SPACING.MOBILE,
    LAYOUT_BREAKPOINTS.SPACING.TABLET,
    LAYOUT_BREAKPOINTS.SPACING.DESKTOP
  ],
  PADDING: [
    LAYOUT_BREAKPOINTS.PADDING.MOBILE,
    LAYOUT_BREAKPOINTS.PADDING.TABLET,
    LAYOUT_BREAKPOINTS.PADDING.DESKTOP
  ]
};

// Single desktop values for components that don't need responsive arrays
export const DESKTOP_WIDTHS = {
  INTENTION_SECTION: LAYOUT_BREAKPOINTS.INTENTION_SECTION.DESKTOP,
  MESSAGES_CARD_MIN_WIDTH: LAYOUT_BREAKPOINTS.INTENTION_SECTION.DESKTOP,
} as const;

/**
 * Sorts intentions by creation date in descending order (newest first)
 */
export const sortIntentionsByDate = (
  intentions: IntentionItem[]
): IntentionItem[] => {
  return intentions
    .slice(0) // Create a shallow copy to avoid mutating original array
    .sort((item1, item2) => Number(item2.createdAt) - Number(item1.createdAt));
};

/**
 * Determines if a message is shared with the current user
 */
export const isMessageSharedWithUser = (intention: IntentionItem): boolean => {
  if (!intention.shared) {
    return false;
  }
  const userEmail = window.localStorage.getItem("userEmail");

  return intention.shared.accessor.email === userEmail;
};

/**
 * Calculates the appropriate right padding for input based on shared status
 */
export const getInputRightPadding = (isSharedWithMe: boolean): string => {
  return isSharedWithMe ? "80px" : "8px";
};

/**
 * Determines if Accept/Decline actions should be shown
 */
export const shouldShowAcceptDeclineActions = (
  isSharedWithMe: boolean,
  approvalStatus: "NEEDS_APPROVAL" | "APPROVED" | "REJECTED" | undefined,
  acceptedMessages: Set<string>,
  intentionId: string
): boolean => {
  // If the message is shared with me and the approval status is NEEDS_APPROVAL, show the actions
  const cond1 = isSharedWithMe
    ? approvalStatus === "NEEDS_APPROVAL"
      ? true
      : false
    : false;

  // If the message is accepted, don't show the actions
  const cond2 = !acceptedMessages.has(intentionId);

  return cond1 && cond2;
};

/**
 * Determines if Share/Delete actions should be shown
 */
export const shouldShowShareDeleteActions = (
  isSharedWithMe: boolean
): boolean => {
  // Always show delete actions for both user-owned and shared intentions
  // Only hide share action for shared intentions (user shouldn't share what's already shared with them)
  return true;
};

/**
 * Determines if Share action should be shown (separate from delete action)
 */
export const shouldShowShareAction = (
  isSharedWithMe: boolean
): boolean => {
  return !isSharedWithMe;
};

/**
 * Gets the appropriate badge color scheme based on acceptance status
 */
export const getBadgeColorScheme = (isAccepted: boolean): string => {
  return isAccepted ? "green" : "blue";
};

/**
 * Prevents event propagation - useful for nested clickable elements
 */
export const stopPropagation = (event: React.MouseEvent): void => {
  event.stopPropagation();
};
